import axios from 'axios';
import config from '@/config';

const api = axios.create({
  baseURL: config.api,
  timeout: config.apiTimeout,
});

const abortController = new AbortController();

api.interceptors.request.use(
  (conf) => {
    const token = localStorage.getItem('token');
    if (token) {
      // eslint-disable-next-line
      conf.headers.Authorization = `bearer ${token}`;
    }
    return conf;
  },
  (error) => Promise.reject(error), // Catch
);

export default {
  api,
  login({ username, password }) {
    return api.post('/auth/login', { username, password });
  },
  logout() {
    return api.post('/auth/logout');
  },
  checkPasswordToDeleteAcc(payload) {
    return api({
      baseURL: `${config.api}`,
      url: `/auth/check-password-to-delete-acc?password=${payload}`,
      data: payload,
      method: 'POST',
    });
  },

  // Countries
  getCountries(payload) {
    return api.post('/countries/get-countries', payload, {
      signal: abortController.signal,
    });
  },
  getCountry(id) {
    return api.get(`/countries/${id}`);
  },

  // Towns
  getTowns(payload = {}) {
    return api.post('/towns/get-towns', payload);
  },

  // Users
  getProfile() {
    return api.get('/users/profile');
  },
  updateProfile(payload) {
    return api.post('/users/create-personal-data-change-request', payload);
  },
  updatePassword(payload) {
    return api({
      url: '/users/update-password',
      data: payload,
      method: 'PUT',
    });
  },
  deactivateUser() {
    return api.post('/users/deactivate');
  },
  updateProfileImage(payload) {
    return api.put('/users/update-profile-image', payload, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  updateShippingInfoProfile(payload) {
    return api.put('/users/update-shipping-info', payload);
  },
  addShippingInfo(payload) {
    return api.post('/users/create-shipping-info', payload);
  },
  verifyEmail({ token, userId }) {
    return api.post(`/users/verify-email?userId=${userId}&token=${token}`);
  },
  register(payload) {
    return api.post('/users/create-b2b-customer-request', payload);
  },
  getShippingAddresses() {
    return api.get('/shipping-address/company');
  },
  getShippingAddressesFiltered(payload) {
    return api({
      url: '/shipping-address/company/filter',
      method: 'POST',
      data: payload,
    });
  },
  createShippingAddress(payload) {
    return api.post('/shipping-address/create-company-shipping-address', payload);
  },
  getShippingAddress(id) {
    return api.get(`/shipping-address/company/${id}`);
  },
  updateShippingAddress(payload) {
    return api.put(`/shipping-address/company/${payload.id}`, payload);
  },
  deleteShippingAddress(id) {
    return api({
      url: `/shipping-address/company/delete-shipping-address/${id}`,
      method: 'DELETE',
    });
  },
  getContactPersons() {
    return api({
      url: '/users/contact-persons',
      method: 'GET',
    });
  },
  // Categories
  getCategories() {
    return api.get('/categories/get-main-page-categories');
  },
  getSimplifiedCategories(payload) {
    return api.post('/categories/simplified', payload);
  },
  getCategoryById(categoryId) {
    return api.post('/categories/get-categories', { categoryId });
  },
  getCategoriesFilter(payload) {
    return api.post('/articles/articles-by-categories-result-filter', payload);
  },

  // Articles
  requestPresentation(articleId) {
    return api({
      method: 'POST',
      url: `/articles/request-presentation/${articleId}`,
    });
  },
  getArticles(payload) {
    return api.post('/articles/main-page-articles', payload);
  },
  getArticleById(articleId) {
    return api.get(`/articles/details/${articleId}`);
  },
  getCustomerCustomizedArticles(payload = {}) {
    const previousRoute = localStorage.getItem('prevRouteName');
    if (!previousRoute || previousRoute !== 'Article') {
      return api.post('/articles/customer-customized-articles', payload);
    }

    return Promise.resolve({ data: { items: [], pager: {} } });
  },
  // getCustomerCustomizedArticlesFilter(payload = {}) {
  //   return api.post('/articles/customer-customized-articles/filters', payload);
  // },
  getNewlyAddedArticles(payload) {
    return api.post('/articles/newly-added-articles', payload);
  },
  getNewlyAddedArticlesFilter(payload) {
    return api({
      method: 'POST',
      url: '/articles/newly-added-articles-filters',
      data: payload,
    });
  },
  getArticlesBySearch(payload) {
    return api.post('/articles/auto-complete-articles', payload);
  },
  getSearchFilters(payload) {
    return api.post('/articles/auto-complete-results-filter', payload);
  },
  getQuickViewArticle(articleId) {
    return api.get(`/articles/quick-view/${articleId}`);
  },
  getRecentArticles(articleIds) {
    return api.post('/articles/recently-viewed', { articleIds });
  },
  getMyArticles(payload) {
    return api({
      method: 'POST',
      url: '/articles/customer/my-articles',
      data: payload,
    });
  },
  getMyArticlesFilter(payload) {
    return api({
      method: 'GET',
      url: '/articles/customer/available-filters-of-my-articles',
      data: payload,
    });
  },
  getArticlesOnSale(payload) {
    return api({
      method: 'GET',
      url: '/product-sales/current-product-sale-no-dates',
      data: payload,
    });
  },

  createOutOfStockRequest(payload) {
    return api({
      url: '/articles/create-article-out-of-stock-request',
      method: 'POST',
      data: payload,
    });
  },

  // Banks
  getBanks(payload) {
    return api.post('/banks/get-banks', payload);
  },
  getBank(id) {
    return api.get(`/banks/${id}`);
  },
  createBank(payload) {
    return api.post('/banks/create-bank', payload);
  },

  // Brands
  getBrands(payload) {
    return api.post('/brands/get-brands', payload);
  },
  getBrand(id) {
    return api.get(`/brands/${id}`);
  },
  getAllBrands() {
    return api.get('/brands');
  },

  // Express posts
  getExpressPosts() {
    return api.get('/express-posts/default');
  },

  getExpressPost(id) {
    return api.get(`/express-posts/${id}`);
  },

  // Interests area
  getInterestsAreas(payload) {
    return api.post('/interests-areas/get-interests-areas', payload);
  },
  getInterestsArea(id) {
    return api.get(`/interests-areas/${id}`);
  },

  // Customer accounts
  getCustomerBankAccounts(payload) {
    return api.post('/customer-bank-accounts/get-customer-bank-accounts', payload);
  },
  getCustomerBankAccount(id) {
    return api.get(`/customer-bank-accounts/${id}`);
  },

  // Blogs
  getBlogs(payload) {
    return api({
      url: '/blogs/get-blogs-for-customers',
      method: 'POST',
      data: {
        ...payload,
        b2b: true,
      },
    });
  },
  getRecentBlogs() {
    return api({
      url: '/blogs/get-recent-blogs-for-customers',
      method: 'GET',
    });
  },
  getBlog(id) {
    return api.get(`/blogs/${id}`);
  },

  // Terms of service
  getTermsOfServices() {
    return api.post('/terms-of-services/get-terms-of-services');
  },

  // Attributes
  getAttributes(payload) {
    return api.post('/attributes/get-attributes', payload);
  },
  getAttribute(id) {
    return api.get(`/attributes/${id}`);
  },
  getAttributesByIds(payload) {
    return api.post('/attributes/get-attributes-by-ids', payload);
  },

  // Variations
  getArticleVariations(id) {
    return api.get(`/variations/${id}`);
  },
  getVariationById(variationId) {
    return api.get(`/variations/${variationId}/details`);
  },

  // Homepage
  getAllJumbotrons() {
    return api.post('/main-page/get-jumbotrons-main', {
      contentType: 1,
    });
  },
  getHighlightedCategories() {
    return api.post('/main-page/main-page-categories', {
      contentType: 1,
    });
  },
  getHighlightedBrand() {
    return api.post('/main-page/main-page-highlighted-brand', {
      contentType: 1,
    });
  },
  getTopBrands() {
    return api.post('/main-page/main-page-highlighted-top-brands', {
      contentType: 1,
    });
  },
  toggleWishlistItem(articleId) {
    return api.post('/wish-list/toggle-item', {
      articleId,
    });
  },
  getWishList() {
    return api.get('/wish-list');
  },

  // Shopping Cart
  addToShoppingCart(payload) {
    return api.post('/shopping-cart/add-item-v2', payload);
  },
  addMultipleItemsToShoppingCart(payload) {
    return api.post('/shopping-cart/add-items', payload);
  },
  getShoppingCart() {
    return api.get('/shopping-cart/customer');
  },
  removeFromCart(shoppingCartItemId) {
    return api.delete(`/shopping-cart/delete-shopping-cart-item/${shoppingCartItemId}`);
  },
  clearCart(id) {
    return api.delete(`/shopping-cart/empty-shopping-cart/${id}`);
  },
  applyDiscountCode(shoppingCartId, code) {
    return api.post(`/shopping-cart/apply-discount-code/${shoppingCartId}/${code}`);
  },
  removeDiscountCode(shoppingCartId) {
    return api.post(`/shopping-cart/remove-discount-code/${shoppingCartId}`);
  },
  removeCartItem(cartItemId) {
    return api.delete(`/shopping-cart/delete-shopping-cart-item/${cartItemId}`);
  },
  updateCart(payload) {
    return api.put('/shopping-cart/update-shopping-cart', payload);
  },
  getShoppingCarts(payload) {
    return api({
      url: '/documents/get-documents',
      data: payload,
      method: 'POST',
    });
  },
  saveShoppingCart(payload) {
    return api({
      url: `/documents/create-document/shopping-cart/${payload.shoppingCartId}`,
      data: payload,
      method: 'POST',
    });
  },
  createShoppingCartFromDocuments(documentId) {
    return api({
      url: `/documents/create-shopping-cart/document/${documentId}`,
      method: 'POST',
    });
  },
  deleteSavedShoppingCarts(ids) {
    return api({
      url: '/documents/delete-documents',
      method: 'DELETE',
      data: ids,
    });
  },

  // Orders
  placeOrder(payload) {
    return api.post('/shopping-cart/checkout', payload);
  },
  getOrders(payload = {}) {
    return api.post('/orders/get-customer-orders', payload);
  },
  getOrder(id) {
    return api.post(`/orders/get-customer-order/${id}`);
  },
  repeatOrder(id) {
    return api.post(`/orders/repeat-order/${id}`);
  },
  updateOrderStatus(payload) {
    return api({
      url: '/orders/update-order-status',
      data: payload,
      method: 'PUT',
    });
  },
  // Reviews
  addReview(payload) {
    return api.post('/articles/add-review', payload);
  },
  getReviews(articleId) {
    return api.post(`/articles/get-reviews/${articleId}`);
  },

  // Messages
  getMessages(payload = {}) {
    return api.post('/system-messages/get-system-messages', payload);
  },
  markMessageAsRead(id) {
    return api.put(`/system-messages/mark-message-as-read/${id}`);
  },
  markAllMessagesAsRead() {
    return api.put('/system-messages/mark-all-messages-as-read');
  },
  getUnreadMessagesCount() {
    return api({
      method: 'GET',
      url: '/system-messages/user/unread-messages',
    });
  },

  // User files
  getUserFiles(payload = {}) {
    return api({
      method: 'POST',
      url: '/user-files/get-company-files',
      data: payload,
    });
  },
  createUserFile(payload) {
    return api({
      url: '/user-files/create-user-file',
      method: 'POST',
      data: payload,
      config: {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    });
  },

  // RMA
  createComplaint(payload) {
    return api({
      url: '/complaints/create-complaint',
      method: 'POST',
      data: payload,
    });
  },
  getComplaints(payload) {
    return api({
      url: '/complaints/get-complaints-by-customer',
      method: 'POST',
      data: payload,
    });
  },

  // Tags
  getMostPopularTags() {
    return api({
      url: '/tags/most-popular-tags',
      method: 'GET',
    });
  },

  // Newsletter
  subscribeToNewsletter(payload) {
    return api({
      url: '/newsletter/send-subscription-request',
      method: 'POST',
      data: payload,
    });
  },
  confirmNewsletterSubscription(payload) {
    return api({
      url: '/newsletter/confirm-subscription',
      method: 'POST',
      data: payload,
    });
  },

  // Reset and forgot password
  forgotPassword(email) {
    return api({
      url: '/users/forgot-password',
      method: 'POST',
      data: {
        userEmail: email,
      },
    });
  },

  resetPassword(payload) {
    return api({
      url: '/users/reset-password',
      method: 'POST',
      data: payload,
    });
  },

  // Parameters
  getFreeShippingAmount() {
    return api({
      url: '/b2b-settings/get-free-shipping-amount',
      method: 'GET',
    });
  },
  getAllowOrderingStockZero() {
    return api({
      url: '/b2b-settings/get-allow-ordering-stock-zero',
    });
  },
  getFreeShippingEnabled() {
    return api({
      url: '/b2b-settings/get-free-shipping-enabled',
      method: 'GET',
    });
  },
  getUseDefaultLanguage() {
    return api({
      url: '/b2b-settings/use-default-language',
      method: 'GET',
    });
  },
  getDefaultLanguage() {
    return api({
      url: '/b2b-settings/default-language',
      method: 'GET',
    });
  },
  getLanguages() {
    return api({
      url: '/b2b-settings/languages',
      method: 'GET',
    });
  },

  // Finances
  getFinances(params) {
    return api({
      url: '/finance/get-finances',
      method: 'GET',
      params,
    });
  },

  getFinanceInfo(params) {
    return api({
      url: '/finance/get-finance-info',
      method: 'GET',
      params,
    });
  },
  abortController,
};
